import { CustomFieldInputs, CustomFieldsView, useCustomFields } from '../components/CustomFields.tsx';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { day, money, parseMoney, todayIso } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { ScoreChip, ScorePanel, ScoringDialog, type Band, type Score } from './LeadScoring.tsx';
import { Kpi, Table } from './Reports.tsx';
import { RecordNotes } from '../components/RecordNotes.tsx';

type StageKind = 'open' | 'won' | 'lost';
const SOURCES = ['phone', 'walk_in', 'web', 'email', 'referral', 'event', 'social', 'other'] as const;
const KINDS = ['call', 'meeting', 'email', 'task', 'note'] as const;
type Source = (typeof SOURCES)[number];
type Kind = (typeof KINDS)[number];

interface Stage {
  id: string;
  name: string;
  sequence: number;
  kind: StageKind;
  probability: number;
  active: boolean;
}
interface Card {
  id: string;
  title: string;
  stage: string;
  value: number;
  probability: number;
  expected_close: string | null;
  priority: number;
  owner_name: string | null;
  party: string | null;
  next_due: string | null;
  next_step: string | null;
  late: boolean;
  score: number | null;
  band: Band | null;
}
interface Pipeline {
  today: string;
  stages: (Stage & { count: number; value: number; weighted: number; leads: Card[] })[];
}
interface Lead {
  id: string;
  title: string;
  customer: string | null;
  contact_name: string | null;
  company_name: string | null;
  phone: string | null;
  email: string | null;
  source: Source;
  owner: string | null;
  owner_name: string | null;
  stage: string;
  value: number;
  probability: number;
  expected_close: string | null;
  priority: number;
  lost_reason: string | null;
  closed_at: string | null;
  quote: string | null;
  note: string | null;
}
interface Activity {
  id: string;
  kind: Kind;
  subject: string;
  due_on: string | null;
  owner_name: string | null;
  done_at: string | null;
  done_by_name: string | null;
  note: string | null;
}
interface LeadDetail {
  lead: Lead;
  stage: Stage;
  stages: Stage[];
  customer: { id: string; name: string; phone: string | null; email: string | null } | null;
  quote: { id: string; name: string; state: string; total: number; order: boolean } | null;
  activities: Activity[];
  today: string;
  score: Score | null;
}
interface AgendaItem {
  id: string;
  kind: Kind;
  subject: string;
  due_on: string;
  owner_name: string | null;
  lead: string;
  lead_title: string;
  party: string | null;
  phone: string | null;
}
interface Person {
  id: string;
  name: string;
}

type Tab = 'pipeline' | 'agenda' | 'report';
const TABS: { key: Tab; icon: IconName }[] = [
  { key: 'pipeline', icon: 'layers' },
  { key: 'agenda', icon: 'calendar' },
  { key: 'report', icon: 'chart' },
];
const KIND_ICON: Record<Kind, IconName> = { call: 'phone', meeting: 'users', email: 'send', task: 'check', note: 'note' };
const STAGE_TONE: Record<StageKind, { tone: Tone; icon: IconName }> = {
  open: { tone: 'info', icon: 'clock' },
  won: { tone: 'ok', icon: 'checkCircle' },
  lost: { tone: 'neutral', icon: 'x' },
};

export function Crm() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'pipeline') as Tab;
  const [open, setOpen] = useState<string | 'new' | null>(() => params.get('lead'));
  const [version, setVersion] = useState(0);
  const changed = () => setVersion((v) => v + 1);
  return (
    <Shell>
      <div className="page reports crm">
        <div className="page-head">
          <div>
            <h1>{t('crm.title')}</h1>
            <p className="muted">{t('crm.subtitle')}</p>
          </div>
          <Button variant="primary" icon="plus" onClick={() => setOpen('new')}>
            {t('crm.new')}
          </Button>
        </div>
        <div className="segmented tabs" role="tablist" aria-label={t('crm.title')}>
          {TABS.map((x) => (
            <button
              key={x.key}
              role="tab"
              aria-selected={tab === x.key}
              className={tab === x.key ? 'active' : ''}
              onClick={() => setParams(x.key === 'pipeline' ? {} : { tab: x.key }, { replace: true })}
            >
              <Icon name={x.icon} size={16} />
              {t(`crm.tab_${x.key}`)}
            </button>
          ))}
        </div>
        {tab === 'agenda' ? (
          <AgendaTab version={version} onOpen={setOpen} />
        ) : tab === 'report' ? (
          <ReportTab version={version} />
        ) : (
          <PipelineTab version={version} onOpen={setOpen} onChanged={changed} />
        )}
      </div>
      {open === 'new' ? (
        <LeadForm
          onClose={() => setOpen(null)}
          onSaved={(id) => {
            changed();
            setOpen(id);
          }}
        />
      ) : open ? (
        <LeadDialog id={open} onClose={() => setOpen(null)} onChanged={changed} />
      ) : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- Pipeline */

function PipelineTab({ version, onOpen, onChanged }: { version: number; onOpen: (id: string) => void; onChanged: () => void }) {
  const { can, me } = useSession();
  const [mine, setMine] = useState(false);
  const [search, setSearch] = useState('');
  const [stagesOpen, setStagesOpen] = useState(false);
  const [scoringOpen, setScoringOpen] = useState(false);
  const [hotOnly, setHotOnly] = useState(false);
  const [ver, setVer] = useState(0);
  const { data } = useAction<Pipeline>('crm.pipeline', { owner: mine ? me?.userId : undefined, search: search.trim() || undefined, v: version + ver * 1000 });
  const scored = !!data?.stages.some((s) => s.leads.some((l) => l.score !== null));
  const shown = (leads: Card[]) => (hotOnly && scored ? leads.filter((l) => l.band === 'hot') : leads);
  const open = data?.stages.filter((s) => s.kind === 'open') ?? [];
  return (
    <div className="crm-wrap">
      <div className="toolbar">
        <div className="toolbar-group">
          <input className="input crm-search" type="search" placeholder={t('crm.search')} aria-label={t('crm.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
          <label className="switch-row crm-mine">
            <span className="switch-title">{t('crm.only_mine')}</span>
            <Switch checked={mine} onChange={setMine} label={t('crm.only_mine')} />
          </label>
          {scored ? (
            <label className="switch-row crm-mine">
              <span className="switch-title">{t('score.hot_only')}</span>
              <Switch checked={hotOnly} onChange={setHotOnly} label={t('score.hot_only')} />
            </label>
          ) : null}
        </div>
        {can('crm.manage') ? (
          <div className="toolbar-group">
            <Button variant="ghost" icon="flame" onClick={() => setScoringOpen(true)}>
              {t('score.button')}
            </Button>
            <Button variant="ghost" icon="settings" onClick={() => setStagesOpen(true)}>
              {t('crm.stages')}
            </Button>
          </div>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={320} r="var(--radius-lg)" />
      ) : (
        <>
          <section className="rep-kpis" aria-label={t('crm.totals')}>
            <Kpi
              label={t('crm.k_open')}
              value={money(open.reduce((s, x) => s + x.value, 0))}
              hint={tn(
                'crm.deals_n',
                open.reduce((s, x) => s + x.count, 0),
              )}
            />
            <Kpi label={t('crm.k_weighted')} value={money(open.reduce((s, x) => s + x.weighted, 0))} hint={t('crm.k_weighted_hint')} />
            <Kpi label={t('crm.k_won')} value={money(data.stages.filter((s) => s.kind === 'won').reduce((s, x) => s + x.value, 0))} hint={t('crm.last_30')} />
            <Kpi
              label={t('crm.k_late')}
              value={String(open.reduce((s, x) => s + x.leads.filter((l) => l.late).length, 0))}
              warn={open.some((x) => x.leads.some((l) => l.late))}
              hint={t('crm.k_late_hint')}
            />
          </section>
          <div className="crm-board" role="list" aria-label={t('crm.tab_pipeline')}>
            {data.stages.map((s) => (
              <section key={s.id} className={`crm-col crm-${s.kind}`} role="listitem" aria-labelledby={`crm-col-${s.id}`}>
                <header>
                  <h2 id={`crm-col-${s.id}`}>
                    {s.name}
                    <span className="crm-count">{s.count}</span>
                  </h2>
                  <span className="muted small tabular">{s.kind === 'open' ? t('crm.col_value', { value: money(s.value), p: s.probability }) : money(s.value)}</span>
                </header>
                {shown(s.leads).map((l) => (
                  <button key={l.id} type="button" className="crm-card" onClick={() => onOpen(l.id)}>
                    <span className="crm-card-top">
                      <span className="crm-card-title">{l.title}</span>
                      {l.priority ? <Stars n={l.priority} /> : null}
                      {l.score !== null && l.band && s.kind === 'open' ? <ScoreChip score={l.score} band={l.band} /> : null}
                    </span>
                    {l.party ? <span className="muted small">{l.party}</span> : null}
                    <span className="crm-card-foot">
                      <span className="tabular strong">{money(l.value)}</span>
                      {l.owner_name ? <span className="muted small">{l.owner_name}</span> : null}
                    </span>
                    {s.kind === 'open' ? (
                      l.next_due ? (
                        <span className={`crm-next small ${l.late ? 'late' : ''}`}>
                          <Icon name={l.late ? 'alert' : 'clock'} size={14} />
                          {l.next_step} · {day(l.next_due)}
                        </span>
                      ) : (
                        <span className="crm-next small none">
                          <Icon name="alert" size={14} />
                          {t('crm.no_next')}
                        </span>
                      )
                    ) : null}
                  </button>
                ))}
                {!shown(s.leads).length ? <p className="muted small crm-empty">{t(s.leads.length ? 'score.col_none_hot' : 'crm.col_empty')}</p> : null}
              </section>
            ))}
          </div>
        </>
      )}
      {scoringOpen ? (
        <ScoringDialog
          onClose={() => setScoringOpen(false)}
          onDone={() => {
            setScoringOpen(false);
            setVer((n) => n + 1);
          }}
        />
      ) : null}
      {stagesOpen && data ? (
        <StagesDialog
          onClose={() => setStagesOpen(false)}
          onDone={() => {
            setStagesOpen(false);
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

function Stars({ n }: { n: number }) {
  return (
    <span className="crm-stars" aria-label={t('crm.priority_n', { n })} title={t('crm.priority_n', { n })}>
      {Array.from({ length: n }, (_, i) => (
        <Icon key={i} name="star" size={14} />
      ))}
    </span>
  );
}

/* ---------------------------------------------------------------- One opportunity */

function LeadDialog({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { data, reload } = useAction<LeadDetail>('crm.lead', { lead: id });
  const [editing, setEditing] = useState(false);
  const [losing, setLosing] = useState<Stage | null>(null);
  const [adding, setAdding] = useState(false);
  const [linking, setLinking] = useState(false);
  const [ver, setVer] = useState(0);
  const { busy, run } = useSave();
  const changed = () => {
    setVer((n) => n + 1);
    void reload();
    onChanged();
  };
  if (!data) {
    return (
      <Dialog title={t('common.loading')} onClose={onClose} size="lg">
        <Skeleton h={300} />
      </Dialog>
    );
  }
  if (editing) return <LeadForm lead={data.lead} onClose={() => setEditing(false)} onSaved={() => (setEditing(false), changed())} />;
  const l = data.lead;
  const closed = data.stage.kind !== 'open';
  const moveTo = async (s: Stage) => {
    if (s.id === l.stage) return;
    if (s.kind === 'lost') return setLosing(s);
    if (await run(() => action('crm.move', { lead: l.id, stage: s.id }), s.kind === 'won' ? t('crm.won_ok') : t('crm.moved'))) changed();
  };
  const contact = [l.contact_name, l.phone, l.email].filter(Boolean);
  const todo = data.activities.filter((a) => !a.done_at);
  const done = data.activities.filter((a) => a.done_at);
  return (
    <Dialog
      title={l.title}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="foot-actions">
            {!closed ? (
              <Button variant="ghost" icon="edit" onClick={() => setEditing(true)}>
                {t('crm.edit')}
              </Button>
            ) : null}
          </span>
          {!closed ? (
            <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
              {t('crm.add_step')}
            </Button>
          ) : (
            <Button icon="note" onClick={() => setAdding(true)}>
              {t('crm.add_note')}
            </Button>
          )}
        </>
      }
    >
      <div className="form-stack crm-detail">
        <div className="crm-stagebar" role="group" aria-label={t('crm.stage')}>
          {data.stages.map((s) => (
            <button key={s.id} type="button" disabled={busy} aria-pressed={s.id === l.stage} className={`crm-step crm-${s.kind}${s.id === l.stage ? ' on' : ''}`} onClick={() => void moveTo(s)}>
              {s.kind === 'won' ? <Icon name="checkCircle" size={14} /> : s.kind === 'lost' ? <Icon name="x" size={14} /> : null}
              {s.name}
            </button>
          ))}
        </div>
        {closed ? (
          <p className={`acc-check ${data.stage.kind === 'won' ? 'ok' : 'off'}`} role="status">
            <Icon name={data.stage.kind === 'won' ? 'checkCircle' : 'x'} size={16} />
            {data.stage.kind === 'won' ? t('crm.is_won', { date: day(l.closed_at!.slice(0, 10)) }) : t('crm.is_lost', { reason: l.lost_reason ?? '' })}
          </p>
        ) : null}
        <section className="rep-kpis crm-kpis" aria-label={t('crm.totals')}>
          <Kpi label={t('crm.value')} value={money(l.value)} hint={t('crm.chance', { p: l.probability })} />
          <Kpi label={t('crm.close_by')} value={l.expected_close ? day(l.expected_close) : '—'} warn={!closed && !!l.expected_close && l.expected_close < data.today} />
          <Kpi label={t('crm.owner')} value={l.owner_name ?? '—'} hint={t(`crm.src_${l.source}`)} />
        </section>
        {data.score && !closed ? <ScorePanel score={data.score} /> : null}
        <CustomFieldsView target="crm.lead" record={l.id} version={ver} />
        <section className="panel" aria-labelledby="crm-who">
          <div className="panel-head">
            <h2 id="crm-who">{t('crm.who')}</h2>
          </div>
          <div className="crm-who">
            <div>
              <p className="strong">{data.customer?.name ?? l.company_name ?? l.contact_name}</p>
              {contact.length ? <p className="muted small">{contact.join(' · ')}</p> : null}
              {l.note ? <p className="muted small">{l.note}</p> : null}
            </div>
            <div className="row-actions">
              {l.phone ? (
                <a className="btn btn-ghost btn-md" href={`tel:${l.phone}`}>
                  <Icon name="phone" size={18} />
                  <span className="btn-label">{t('crm.call')}</span>
                </a>
              ) : null}
              {!data.customer ? (
                <Button variant="secondary" icon="users" busy={busy} onClick={async () => (await run(() => action('crm.make_customer', { lead: l.id }), t('crm.customer_made'))) && changed()}>
                  {t('crm.make_customer')}
                </Button>
              ) : null}
            </div>
          </div>
          <div className="crm-quote">
            {data.quote ? (
              <>
                <span>
                  <Icon name="invoice" size={16} /> {t(data.quote.order ? 'crm.order_is' : 'crm.quote_is', { name: data.quote.name, total: money(data.quote.total) })}
                </span>
                {!closed ? (
                  <Button variant="ghost" onClick={async () => (await run(() => action('crm.link_quote', { lead: l.id, quote: null }))) && changed()}>
                    {t('crm.unlink')}
                  </Button>
                ) : null}
              </>
            ) : data.customer && !closed ? (
              <>
                <span className="muted small">{t('crm.no_quote')}</span>
                <Button variant="ghost" icon="link" onClick={() => setLinking(true)}>
                  {t('crm.link_quote')}
                </Button>
              </>
            ) : null}
          </div>
        </section>
        <section className="panel" aria-labelledby="crm-steps">
          <div className="panel-head">
            <h2 id="crm-steps">{t('crm.next_steps')}</h2>
          </div>
          {!todo.length ? (
            <p className="muted small">{closed ? t('crm.closed_steps') : t('crm.no_next_long')}</p>
          ) : (
            <ul className="crm-acts">
              {todo.map((a) => (
                <ActivityRow key={a.id} a={a} today={data.today} onChanged={changed} />
              ))}
            </ul>
          )}
        </section>
        {done.length ? (
          <section className="panel" aria-labelledby="crm-history">
            <div className="panel-head">
              <h2 id="crm-history">{t('crm.history')}</h2>
            </div>
            <ul className="crm-acts">
              {done.map((a) => (
                <ActivityRow key={a.id} a={a} today={data.today} onChanged={changed} />
              ))}
            </ul>
          </section>
        ) : null}
        <RecordNotes model="crm.lead" record={l.id} version={ver} />
      </div>
      {losing ? (
        <LoseDialog
          lead={l.id}
          stage={losing}
          onClose={() => setLosing(null)}
          onDone={() => {
            setLosing(null);
            changed();
          }}
        />
      ) : null}
      {adding ? (
        <ActivityForm
          lead={l.id}
          noteOnly={closed}
          onClose={() => setAdding(false)}
          onDone={() => {
            setAdding(false);
            changed();
          }}
        />
      ) : null}
      {linking ? (
        <LinkQuote
          lead={l.id}
          onClose={() => setLinking(false)}
          onDone={() => {
            setLinking(false);
            changed();
          }}
        />
      ) : null}
    </Dialog>
  );
}

function ActivityRow({ a, today, onChanged }: { a: Activity; today: string; onChanged: () => void }) {
  const { busy, run } = useSave();
  const late = !a.done_at && !!a.due_on && a.due_on < today;
  return (
    <li className={`crm-act${a.done_at ? ' done' : ''}${late ? ' late' : ''}`}>
      <span className="crm-act-icon" aria-hidden="true">
        <Icon name={KIND_ICON[a.kind]} size={16} />
      </span>
      <span className="crm-act-main">
        <span className="strong">{a.subject}</span>
        <span className="muted small">
          {[
            t(`crm.k_${a.kind}`),
            a.done_at
              ? t('crm.done_by', { name: a.done_by_name ?? '', date: day(a.done_at.slice(0, 10)) })
              : a.due_on
                ? late
                  ? t('crm.was_due', { date: day(a.due_on) })
                  : t('crm.due', { date: day(a.due_on) })
                : null,
            !a.done_at ? a.owner_name : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
        {a.note ? <span className="small">{a.note}</span> : null}
      </span>
      {!a.done_at ? (
        <span className="row-actions">
          <IconButton icon="trash" label={t('crm.remove_step')} disabled={busy} onClick={async () => (await run(() => action('crm.remove_activity', { activity: a.id }))) && onChanged()} />
          <Button variant="secondary" icon="check" busy={busy} onClick={async () => (await run(() => action('crm.done_activity', { activity: a.id }), t('crm.step_done'))) && onChanged()}>
            {t('crm.mark_done')}
          </Button>
        </span>
      ) : null}
    </li>
  );
}

function LoseDialog({ lead, stage, onClose, onDone }: { lead: string; stage: Stage; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const { busy, run } = useSave();
  return (
    <Dialog
      title={t('crm.lose_title')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!reason.trim()}
            onClick={async () => (await run(() => action('crm.move', { lead, stage: stage.id, reason: reason.trim() }), t('crm.lost_ok'))) && onDone()}
          >
            {t('crm.lose')}
          </Button>
        </>
      }
    >
      <Field label={t('crm.lost_why')} hint={t('crm.lost_hint')} required>
        <textarea className="input" rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} data-autofocus />
      </Field>
    </Dialog>
  );
}

function ActivityForm({ lead, noteOnly, onClose, onDone }: { lead: string; noteOnly: boolean; onClose: () => void; onDone: () => void }) {
  const [kind, setKind] = useState<Kind>(noteOnly ? 'note' : 'call');
  const [subject, setSubject] = useState('');
  const [due, setDue] = useState(todayIso());
  const [note, setNote] = useState('');
  const { busy, run } = useSave();
  const ok = subject.trim() && (kind === 'note' || due);
  const save = async () => {
    if (ok && (await run(() => action('crm.save_activity', { lead, kind, subject: subject.trim(), due_on: kind === 'note' ? null : due, note: note.trim() || null }), t('crm.step_saved')))) onDone();
  };
  return (
    <Dialog
      title={noteOnly ? t('crm.add_note') : t('crm.add_step')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!ok} onClick={save}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        {!noteOnly ? (
          <div className="segmented crm-kinds" role="radiogroup" aria-label={t('crm.kind')}>
            {KINDS.map((k) => (
              <button key={k} type="button" role="radio" aria-checked={kind === k} className={kind === k ? 'active' : ''} onClick={() => setKind(k)}>
                <Icon name={KIND_ICON[k]} size={16} />
                {t(`crm.k_${k}`)}
              </button>
            ))}
          </div>
        ) : null}
        <Field label={kind === 'note' ? t('crm.note_what') : t('crm.step_what')} required>
          <input className="input" value={subject} maxLength={160} onChange={(e) => setSubject(e.target.value)} data-autofocus />
        </Field>
        {kind !== 'note' ? (
          <Field label={t('crm.due_on')} required>
            <input className="input" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          </Field>
        ) : null}
        <Field label={t('crm.details')}>
          <textarea className="input" rows={3} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function LinkQuote({ lead, onClose, onDone }: { lead: string; onClose: () => void; onDone: () => void }) {
  const { data } = useAction<{ id: string; name: string; date: string; state: string; total: number }[]>('crm.quotes', { lead });
  const { busy, run } = useSave();
  return (
    <Dialog title={t('crm.link_quote')} onClose={onClose} size="sm">
      {!data ? (
        <Skeleton h={120} />
      ) : !data.length ? (
        <EmptyState icon="invoice" title={t('crm.no_quotes')} body={t('crm.no_quotes_body')} />
      ) : (
        <ul className="dst-pick">
          {data.map((q) => (
            <li key={q.id}>
              <button type="button" disabled={busy} onClick={async () => (await run(() => action('crm.link_quote', { lead, quote: q.id }), t('crm.linked'))) && onDone()}>
                <span>
                  {q.name} · {day(q.date)}
                </span>
                <span className="tabular strong">{money(q.total)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

function LeadForm({ lead, onClose, onSaved }: { lead?: Lead; onClose: () => void; onSaved: (id: string) => void }) {
  const { data: people } = useAction<Person[]>('crm.people', {});
  const { data: customers } = useAction<{ id: string; name: string; active: boolean }[]>('customers.list', {});
  const { me } = useSession();
  const [f, setF] = useState({
    title: lead?.title ?? '',
    customer: lead?.customer ?? '',
    contact_name: lead?.contact_name ?? '',
    company_name: lead?.company_name ?? '',
    phone: lead?.phone ?? '',
    email: lead?.email ?? '',
    source: lead?.source ?? ('phone' as Source),
    owner: lead?.owner ?? me?.userId ?? '',
    value: lead?.value ? String(lead.value / 100) : '',
    probability: lead ? String(lead.probability) : '',
    expected_close: lead?.expected_close ?? '',
    priority: lead?.priority ?? 0,
    note: lead?.note ?? '',
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const { busy, run } = useSave();
  const cf = useCustomFields('crm.lead', lead?.id);
  const [tried, setTried] = useState(false);
  const value = f.value.trim() ? parseMoney(f.value) : 0;
  const prob = f.probability.trim() ? Number(f.probability) : undefined;
  const probOk = prob === undefined || (Number.isInteger(prob) && prob >= 0 && prob <= 100);
  const ok = f.title.trim() && (f.customer || f.contact_name.trim() || f.company_name.trim()) && value !== null && value >= 0 && probOk;
  const save = async () => {
    setTried(true);
    if (!ok || !cf.valid) return;
    let id = lead?.id ?? '';
    const saved = await run(async () => {
      const r = await action<{ id: string }>('crm.save_lead', {
        lead: lead?.id,
        title: f.title.trim(),
        customer: f.customer || null,
        contact_name: f.contact_name,
        company_name: f.company_name,
        phone: f.phone,
        email: f.email,
        source: f.source,
        owner: f.owner || null,
        value,
        probability: prob,
        expected_close: f.expected_close || null,
        priority: f.priority,
        note: f.note,
      });
      id = r.id;
      await cf.save(id);
    }, t('crm.saved'));
    if (saved) onSaved(id);
  };
  return (
    <Dialog
      title={lead ? t('crm.edit') : t('crm.new')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!ok} onClick={save}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('crm.f_title')} hint={t('crm.f_title_hint')} required>
          <input className="input" value={f.title} maxLength={120} onChange={(e) => set('title', e.target.value)} data-autofocus />
        </Field>
        <Field label={t('crm.f_customer')} hint={t('crm.f_customer_hint')}>
          <select className="input" value={f.customer} onChange={(e) => set('customer', e.target.value)}>
            <option value="">{t('crm.new_contact')}</option>
            {(customers ?? [])
              .filter((c) => c.active !== false || c.id === f.customer)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
        </Field>
        <div className="dn-fields">
          <Field label={t('crm.f_contact')}>
            <input className="input" value={f.contact_name} maxLength={120} onChange={(e) => set('contact_name', e.target.value)} />
          </Field>
          <Field label={t('crm.f_company')}>
            <input className="input" value={f.company_name} maxLength={120} onChange={(e) => set('company_name', e.target.value)} />
          </Field>
          <Field label={t('crm.f_phone')}>
            <input className="input" type="tel" value={f.phone} maxLength={40} onChange={(e) => set('phone', e.target.value)} />
          </Field>
          <Field label={t('crm.f_email')}>
            <input className="input" type="email" value={f.email} maxLength={120} onChange={(e) => set('email', e.target.value)} />
          </Field>
          <Field label={t('crm.value')} hint={t('crm.f_value_hint')} error={value === null ? t('prl.amount_bad') : null}>
            <input className="input tabular" inputMode="decimal" value={f.value} placeholder="0" onChange={(e) => set('value', e.target.value)} />
          </Field>
          <Field label={t('crm.f_prob')} hint={t('crm.f_prob_hint')} error={probOk ? null : t('crm.f_prob_bad')}>
            <input className="input tabular" inputMode="numeric" value={f.probability} placeholder={t('crm.from_stage')} onChange={(e) => set('probability', e.target.value.replace(/\D/g, ''))} />
          </Field>
          <Field label={t('crm.close_by')}>
            <input className="input" type="date" value={f.expected_close} onChange={(e) => set('expected_close', e.target.value)} />
          </Field>
          <Field label={t('crm.f_source')}>
            <select className="input" value={f.source} onChange={(e) => set('source', e.target.value as Source)}>
              {SOURCES.map((s) => (
                <option key={s} value={s}>
                  {t(`crm.src_${s}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('crm.owner')}>
            <select className="input" value={f.owner} onChange={(e) => set('owner', e.target.value)}>
              <option value="">{t('crm.nobody')}</option>
              {(people ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <fieldset className="dn-owner">
          <legend>{t('crm.priority')}</legend>
          <div className="segmented" role="radiogroup">
            {([0, 1, 2, 3] as const).map((p) => (
              <button key={p} type="button" role="radio" aria-checked={f.priority === p} className={f.priority === p ? 'active' : ''} onClick={() => set('priority', p)}>
                {t(`crm.prio_${p}`)}
              </button>
            ))}
          </div>
        </fieldset>
        <Field label={t('crm.details')}>
          <textarea className="input" rows={3} maxLength={2000} value={f.note} onChange={(e) => set('note', e.target.value)} />
        </Field>
        <CustomFieldInputs cf={cf} tried={tried} />
      </div>
    </Dialog>
  );
}

function StagesDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { data } = useAction<Stage[]>('crm.stages', {});
  const [rows, setRows] = useState<(Omit<Stage, 'id' | 'sequence'> & { id?: string; key: string })[] | null>(null);
  const { busy, run } = useSave();
  const list = rows ?? data?.map((s) => ({ ...s, key: s.id })) ?? null;
  const edit = (i: number, patch: Partial<NonNullable<typeof list>[number]>) => list && setRows(list.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const move = (i: number, by: number) => {
    if (!list) return;
    const next = [...list];
    const [x] = next.splice(i, 1);
    next.splice(i + by, 0, x!);
    setRows(next);
  };
  const save = async () => {
    if (!list) return;
    if (await run(() => action('crm.save_stages', { stages: list.map(({ id, name, kind, probability, active }) => ({ id, name: name.trim(), kind, probability, active })) }), t('crm.stages_saved')))
      onDone();
  };
  return (
    <Dialog
      title={t('crm.stages')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" icon="plus" onClick={() => list && setRows([...list, { key: `n${list.length}`, name: '', kind: 'open', probability: 50, active: true }])}>
            {t('crm.add_stage')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!list || list.some((r) => !r.name.trim())} onClick={save}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      {!list ? (
        <Skeleton h={240} />
      ) : (
        <div className="form-stack">
          <p className="muted small">{t('crm.stages_hint')}</p>
          <ol className="crm-stage-rows">
            {list.map((s, i) => (
              <li key={s.key}>
                <input className="input" aria-label={t('crm.stage_name')} value={s.name} maxLength={60} onChange={(e) => edit(i, { name: e.target.value })} />
                <select className="input" aria-label={t('crm.stage_kind')} value={s.kind} onChange={(e) => edit(i, { kind: e.target.value as StageKind })}>
                  {(['open', 'won', 'lost'] as const).map((k) => (
                    <option key={k} value={k}>
                      {t(`crm.kind_${k}`)}
                    </option>
                  ))}
                </select>
                <span className="crm-prob">
                  <input
                    className="input tabular"
                    inputMode="numeric"
                    aria-label={t('crm.f_prob')}
                    value={String(s.probability)}
                    onChange={(e) => edit(i, { probability: Math.min(100, Number(e.target.value.replace(/\D/g, '') || 0)) })}
                  />
                  %
                </span>
                <Switch checked={s.active} onChange={(v) => edit(i, { active: v })} label={t('crm.in_use')} />
                <span className="row-actions">
                  <IconButton icon="up" label={t('crm.up')} onClick={() => move(i, -1)} disabled={i === 0} />
                  <IconButton icon="down" label={t('crm.down')} onClick={() => move(i, 1)} disabled={i === list.length - 1} />
                  <IconButton icon="trash" label={t('crm.remove_stage')} onClick={() => setRows(list.filter((_, j) => j !== i))} />
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Agenda */

function AgendaTab({ version, onOpen }: { version: number; onOpen: (id: string) => void }) {
  const { me } = useSession();
  const [all, setAll] = useState(false);
  const { data } = useAction<{ today: string; late: AgendaItem[]; today_items: AgendaItem[]; soon: AgendaItem[] }>('crm.agenda', { owner: all ? null : me?.userId, v: version });
  if (!data) return <Skeleton h={240} r="var(--radius-lg)" />;
  const groups: [string, AgendaItem[], boolean][] = [
    [t('crm.ag_late'), data.late, true],
    [t('crm.ag_today'), data.today_items, false],
    [t('crm.ag_soon'), data.soon, false],
  ];
  const empty = !data.late.length && !data.today_items.length && !data.soon.length;
  return (
    <div className="rep-grid">
      <div className="toolbar">
        <label className="switch-row crm-mine">
          <span className="switch-title">{t('crm.everyone')}</span>
          <Switch checked={all} onChange={setAll} label={t('crm.everyone')} />
        </label>
      </div>
      {empty ? (
        <div className="rep-wide">
          <EmptyState icon="calendar" title={t('crm.ag_empty')} body={t('crm.ag_empty_body')} />
        </div>
      ) : (
        groups
          .filter(([, items]) => items.length)
          .map(([title, items, late]) => (
            <section key={title} className="panel rep-wide" aria-label={title}>
              <div className="panel-head">
                <h2>
                  {title} <span className="crm-count">{items.length}</span>
                </h2>
              </div>
              <ul className="crm-acts">
                {items.map((a) => (
                  <li key={a.id} className={`crm-act${late ? ' late' : ''}`}>
                    <span className="crm-act-icon" aria-hidden="true">
                      <Icon name={KIND_ICON[a.kind]} size={16} />
                    </span>
                    <button type="button" className="crm-act-main crm-link" onClick={() => onOpen(a.lead)}>
                      <span className="strong">{a.subject}</span>
                      <span className="muted small">{[a.lead_title, a.party, day(a.due_on), all ? a.owner_name : null].filter(Boolean).join(' · ')}</span>
                    </button>
                    {a.phone && a.kind === 'call' ? (
                      <a className="icon-btn btn-ghost" href={`tel:${a.phone}`} aria-label={t('crm.call')} title={t('crm.call')}>
                        <Icon name="phone" />
                      </a>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ))
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- Report */

type Sum = { won: number; lost: number; open: number; won_value: number; open_value: number; weighted: number; win_rate: number | null; days_to_win: number | null };

function ReportTab({ version }: { version: number }) {
  const [days, setDays] = useState(90);
  const { data } = useAction<{ totals: Sum; by_owner: (Sum & { key: string; label: string | null })[]; by_source: (Sum & { key: string })[]; lost_reasons: { reason: string | null; n: number }[] }>(
    'crm.report',
    { days, v: version },
  );
  const rows = (list: (Sum & { key: string })[], name: (r: Sum & { key: string; label?: string | null }) => string) =>
    list.map((r) => ({
      key: r.key || 'none',
      cells: [
        <span className="strong">{name(r)}</span>,
        <span className="tabular">{r.won}</span>,
        <span className="tabular">{r.lost}</span>,
        <span className="tabular">{r.win_rate === null ? '—' : `${r.win_rate} %`}</span>,
        <span className="tabular strong">{money(r.won_value)}</span>,
        <span className="tabular">{money(r.open_value)}</span>,
      ],
    }));
  const head = [t('crm.r_name'), t('crm.r_won'), t('crm.r_lost'), t('crm.r_rate'), t('crm.r_won_value'), t('crm.r_open_value')];
  return (
    <div className="rep-grid">
      <div className="toolbar">
        <div className="segmented" role="radiogroup" aria-label={t('crm.r_period')}>
          {[30, 90, 365].map((d) => (
            <button key={d} type="button" role="radio" aria-checked={days === d} className={days === d ? 'active' : ''} onClick={() => setDays(d)}>
              {t('crm.r_days', { n: d })}
            </button>
          ))}
        </div>
      </div>
      {!data ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : (
        <>
          <section className="rep-kpis" aria-label={t('crm.totals')}>
            <Kpi label={t('crm.r_rate')} value={data.totals.win_rate === null ? '—' : `${data.totals.win_rate} %`} hint={t('crm.r_rate_hint', { won: data.totals.won, lost: data.totals.lost })} />
            <Kpi label={t('crm.r_won_value')} value={money(data.totals.won_value)} />
            <Kpi label={t('crm.k_weighted')} value={money(data.totals.weighted)} hint={t('crm.r_open_hint', { value: money(data.totals.open_value) })} />
            <Kpi label={t('crm.r_days_to_win')} value={data.totals.days_to_win === null ? '—' : tn('crm.days_n', data.totals.days_to_win)} />
          </section>
          <section className="panel rep-wide" aria-labelledby="crm-r-owner">
            <div className="panel-head">
              <h2 id="crm-r-owner">{t('crm.r_by_owner')}</h2>
            </div>
            <Table head={head} phoneHide={[2, 3, 5]} empty={t('crm.r_empty')} rows={rows(data.by_owner, (r) => r.label ?? t('crm.nobody'))} />
          </section>
          <section className="panel" aria-labelledby="crm-r-source">
            <div className="panel-head">
              <h2 id="crm-r-source">{t('crm.r_by_source')}</h2>
            </div>
            <Table head={head} phoneHide={[2, 3, 5]} empty={t('crm.r_empty')} rows={rows(data.by_source, (r) => t(`crm.src_${r.key as Source}`))} />
          </section>
          <section className="panel" aria-labelledby="crm-r-lost">
            <div className="panel-head">
              <h2 id="crm-r-lost">{t('crm.r_lost_reasons')}</h2>
            </div>
            <Table
              head={[t('crm.r_reason'), t('crm.r_count')]}
              empty={t('crm.r_no_lost')}
              rows={data.lost_reasons.map((r, i) => ({ key: String(i), cells: [r.reason ?? '—', <span className="tabular">{r.n}</span>] }))}
            />
          </section>
        </>
      )}
    </div>
  );
}

export const crmStageBadge = (kind: StageKind, label: string) => <StatusBadge tone={STAGE_TONE[kind].tone} icon={STAGE_TONE[kind].icon} label={label} />;
